import { defineStore, acceptHMRUpdate } from 'pinia'
import { apiClient } from '@/helpers/api.js'
import { handleApiError } from '@/services/errorHandler'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useCardStore } from '@/stores/CardStore'
import {
  LISTING_STATUS,
  LISTED_STATUS,
  POLICIES_IDS_NAMES_IN_TEMPLATE,
  AVAILABLE_FILTERS,
  LISTINGS_END_ENDPOINT,
} from '@/constants'
import { stripEmoji } from '@/helpers/emoji'
import { useUserStore } from '@/stores/UserStore'

export const useListingsStore = defineStore('ListingsStore', {
  state: () => {
    return {
      activeConnectionData: null,
      listingsLoading: false,
      listingsLoadingMore: false,
      listingsDetailsLoading: false,
      additionalInfoLoader: false,
      prevNextIdsLoader: false,
      additionalInfoAlreadyFetched: false,
      correctListingsLoader: false,
      endListingsLoading: false,
      // True from the moment a BULK end is queued until the `listings_ended`
      // Pusher event lands. `usePusher` reads it to tell a bulk end apart from a
      // single-listing end: the bulk flow owns its own progress/completed toast
      // and needs the inventory grid refetched, whereas the single flow shows
      // the "Your listing has ended." toast and patches selectedCardData.
      bulkEndInFlight: false,
      listingsView: 'List',
      openedListingDialog: false,
      selectedCardDataWithBeforeUpdate: {},
      listings: [],
      originalListings: [],
      endedListings: [],
      totalListings: {
        for_sale: 0,
        ended: 0
      },
      pages: {
        for_sale: 1,
        ended: 1
      },
      count: {
        collx: 0,
        ebay: 0,
        whatnot: 0,
        shopify: 0,
      },
      nextListingId: null,
      previousListingId: null,
      isDirty: false,
      visibleListingList: '',
      setValuesToDefault: false,
      // ─── Bulk selection (CDP-2459) ──────────────────────────────────────────
      // Keyed by listing id so toggling is O(1) and a selection survives "Load
      // more". Which SECTION an item belongs to is derived from the rendered
      // lists (below) rather than stored, so it can never drift from the UI.
      selectedListings: {},
    }
  },

  getters: {
    listingsByStatus () {
      return {
        for_sale: { key: 'for_sale', values: this.listings },
        ended: { key: 'ended', values: this.endedListings }
      }
    },

    selectedListingsCount: (state) => Object.keys(state.selectedListings).length,

    // Split by section: Relist applies to ended items, End Listings to live ones.
    selectedForSaleListings: (state) => state.listings.filter(item => state.selectedListings[item.id]),
    selectedEndedListings: (state) => state.endedListings.filter(item => state.selectedListings[item.id]),
  },

  actions: {
    // ─── Bulk selection (CDP-2459) ────────────────────────────────────────────

    isListingSelected(id) {
      return !!this.selectedListings[id]
    },

    toggleListingSelection(listing) {
      if (this.selectedListings[listing.id]) {
        delete this.selectedListings[listing.id]
        return
      }

      this.selectedListings[listing.id] = listing
    },

    /** Select-all / clear-all for one section ('for_sale' | 'ended'). */
    toggleListingsSection(sectionKey, selected) {
      const items = this.listingsByStatus[sectionKey]?.values || []

      items.forEach(item => {
        if (selected) {
          this.selectedListings[item.id] = item
        } else {
          delete this.selectedListings[item.id]
        }
      })
    },

    clearListingsSelection() {
      this.selectedListings = {}
    },

    async fetchListings(type, section = 'for_sale', isPagination = false) {
      if (type === 'empty') return

      const cardStore = useCardStore()

      // A fresh list (connection switch, filter, sort) replaces what's on screen,
      // so anything selected is gone — drop it. "Load more" keeps the selection.
      if (!isPagination) this.clearListingsSelection()

      try {
        this.fetchListingsAdditionalInfo()

        // Set appropriate loading state based on pagination
        if (this.pages[section] > 1) {
          this.listingsLoadingMore = true
        } else {
          this.listingsLoading = true
        }

        let listingList = { data: { available_filters: AVAILABLE_FILTERS } }
        let endedListingList = { data: { available_filters: AVAILABLE_FILTERS } }

        delete cardStore.itemFilters.status

        if (!this.visibleListingList || this.visibleListingList === 'for_sale') {
          if (this.visibleListingList === 'for_sale') {
            this.endedListings = []
          }

          // Base parameters for API request
          const baseParams = {
            page: this.pages[section],
            perPage: 10,
            type
          }

          // Combine all filters into a single filter parameter with comma separation, excluding 'q'
          const filterParams = Object.entries(cardStore.itemFilters).length 
            ? `filter=${Object.entries(cardStore.itemFilters)
                .filter(([key]) => key !== 'q')
                .map(([key, value]) => `${key}=${value}`)
                .join(',')}`
            : ''

          const url = `/listings${filterParams ? '?' + filterParams : ''}${
            Object.keys(baseParams).length ? (filterParams ? '&' : '?') + new URLSearchParams({
              ...baseParams,
              ...(cardStore.itemFilters.q ? { q: cardStore.itemFilters.q } : {})
            }).toString() : ''
          }`

          // Fetch active listings
          listingList = await apiClient().get(url)

          // Process active listings data
          const updatedListings = listingList.data.items.reduce((acc, item) => {
            // Merge card and listing data, add status fields
            acc.push({ 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              listingStatus: LISTING_STATUS.for_sale.key,
              // The listing spread wins on `id`, so keep the CARD id — the
              // end/relist endpoints take card_ids.
              cardId: item.card.id,
              // What the shared bulk-listing helpers classify on (see
              // helpers/bulkListings.js). This section IS "live on this marketplace".
              [`${type}ListedStatus`]: LISTED_STATUS.LISTED,
            })
            return acc
          }, [])

          // Update active listings state - either append or replace
          this.listings = isPagination 
            ? [...this.listings, ...updatedListings] 
            : updatedListings

          this.totalListings.for_sale = listingList.data.total_pages
        }


        if (!this.visibleListingList || this.visibleListingList === 'ended') {
          if (this.visibleListingList === 'ended') {
            this.listings = []
          }
          // Construct URL for ended listings using same filters
          const endedFilterParams = Object.entries(cardStore.itemFilters).length
          ? `filter=${Object.entries(cardStore.itemFilters)
              .filter(([key]) => key !== 'q')
              .map(([key, value]) => `${key}=${value}`)
              .join(',')}`
          : ''

          const endedBaseParams = {
            ended: true,
            perPage: 10,
            page: this.pages[section],
            type
          }

          const endedUrl = `/listings${endedFilterParams ? '?' + endedFilterParams : ''}${
          Object.keys(endedBaseParams).length ? (endedFilterParams ? '&' : '?') + new URLSearchParams({
            ...endedBaseParams,
            ...(cardStore.itemFilters.q ? { q: cardStore.itemFilters.q } : {})
          }).toString() : ''
          }`

          // Fetch ended listings
          endedListingList = await apiClient().get(endedUrl)

          // Process ended listings data
          const updatedEndedListings = endedListingList.data.items.reduce((acc, item) => {
            // Merge card and listing data, add status fields
            acc.push({ 
              ...item.card, 
              ...item.listing, 
              status: item.card.status, 
              listingStatus: LISTING_STATUS.ended.key,
              cardId: item.card.id,
              [`${type}ListedStatus`]: LISTED_STATUS.ENDED,
            })
            return acc
          }, [])
          
          // Update ended listings state - either append or replace
          this.endedListings = isPagination
            ? [...this.endedListings, ...updatedEndedListings]
            : updatedEndedListings

          this.totalListings.ended = endedListingList.data.total_pages
        }

        // Reset loading states
        this.listingsLoading = false
        this.listingsLoadingMore = false

      } catch (error) {
        handleApiError(error, { component: 'ListingsStore', action: 'fetchListings' })
        // Reset loading states on error
        this.listingsLoading = false
        this.listingsLoadingMore = false
      }
    },

    async fetchListingsAdditionalInfo() {
      const cardStore = useCardStore()

      try {
        if (this.additionalInfoAlreadyFetched) return

        this.additionalInfoLoader = true

        const { data } = await apiClient().get(`/listings/info`)

        // Update count state which we show on sidebar menu
        this.count = Object.entries(this.count).reduce((acc, [key, value]) => {
          acc[key] = data[`${key}_listings_active`] + data[`${key}_listings_inactive`]
          return acc
        }, {})

        cardStore.availableFilters = {
          attribute: [ ...data.available_filters?.attribute ],
          brand: [ ...data.available_filters?.brand ],
          category: [ ...data.available_filters?.category ],
          graded: [ ...data.available_filters?.graded ],
          grader: [ ...data.available_filters?.grader ],
          team: [ ...data.available_filters?.team ],
          status: { ...data.available_filters?.status },
        }

        if (this.activeConnectionData) {
          this.totalListings.for_sale = data[`${this.activeConnectionData.key}_listings_active`]
          this.totalListings.ended = data[`${this.activeConnectionData.key}_listings_inactive`]
        }

        this.additionalInfoLoader = false
        this.additionalInfoAlreadyFetched = true
      } catch (error) {
        this.additionalInfoLoader = false
        handleApiError(error, { component: 'ListingsStore', action: 'fetchListingsAdditionalInfo' })
      }
    },

    async fetchListingById(type, id) {
      const connectionStore = useConnectionStore()
      const userStore = useUserStore()
      const cardStore = useCardStore()

      try {
        this.listingsDetailsLoading = true

        const savedData = JSON.parse(localStorage.getItem('savedListingData')) || {}

        const { data } = await apiClient().get(`/listings/${id}?type=${type}`)

        if (!data) return

        const listingStatus = data.listing?.[`${this.activeConnectionData.key}_status`] || LISTING_STATUS.for_sale.key

        cardStore.selectedCardData = {
          ...data,
          ...data.listing, 
          ...data.card, 
          status: data.card.status, 
          id: data.card.id,
          set: data.card.set,
          number: data.card.number,
          title: data.card.title,
          description: data.card.description,
          player: data.card.player,
          team: data.card.team,
          year: data.card.year,
          listingStatus,
          listingId: data.listing.id,
          attributes: data.card.flags,
          readonlyListing: data.listing.read_only,
          ebayListingId: data.listing.ebay_listing_id,
          whatnotListingId: data.listing.whatnot_id,
          collx_price: data.listing.collx_price,
          ebay_price: data.listing.ebay_price,
          whatnot_price: data.listing.whatnot_price,
          shopify_price: data.listing.shopify_price,
          has_price_recommendation: data.card.has_price_recommendation,
          price_recommendation: data.card.price_recommendation,
          ebayQuantityAvailable: data.listing.ebay_quantity_available,
          whatnotQuantityAvailable: data.listing.whatnot_quantity_available,
          collxQuantityAvailable: data.listing.collx_quantity_available,
          shopifyQuantityAvailable: data.listing.shopify_quantity_available,
          ebayQuantitySold: data.listing.ebay_quantity_sold,
          whatnotQuantitySold: data.listing.whatnot_quantity_sold,
          collxQuantitySold: data.listing.collx_quantity_sold,
          shopifyQuantitySold: data.listing.shopify_quantity_sold,
          collxListingUrl: data.listing.collx_listing_url,
        }

        this.selectedCardDataWithBeforeUpdate = { 
          ...cardStore.selectedCardData, 
          collx_quantity_available: data.listing.collx_quantity_available,
          ebay_quantity_available: data.listing.ebay_quantity_available,
          whatnot_quantity_available: data.listing.whatnot_quantity_available,
        }

        if (savedData[userStore.user.id] && savedData[userStore.user.id][cardStore.selectedCardData.id]) {
          cardStore.selectedCardData = { ...cardStore.selectedCardData, ...savedData[userStore.user.id][cardStore.selectedCardData.id] }
          this.isDirty = true
        }

        connectionStore.selectedTemplate = {
          ebay_location_key: cardStore.selectedCardData.location_key,
          ebay_location_id: cardStore.selectedCardData.location_id,
          [POLICIES_IDS_NAMES_IN_TEMPLATE.fulfillmentPolicies]: cardStore.selectedCardData.fulfillment_id,
          [POLICIES_IDS_NAMES_IN_TEMPLATE.paymentPolicies]: cardStore.selectedCardData.payment_id,
          [POLICIES_IDS_NAMES_IN_TEMPLATE.returnPolicies]: cardStore.selectedCardData.return_id,
        }

        this.fetchNextPrevIds()
        
        this.listingsDetailsLoading = false

        //this.fetchListings(type, listingStatus)
      } catch (error) { 
        handleApiError(error, { component: 'ListingsStore', action: 'fetchListingById' })
        this.listingsDetailsLoading = false
      }
    },

    async updateListingLocally(body) {
      const cardStore = useCardStore()

      try {
        cardStore.selectedCardData = { ...cardStore.selectedCardData, ...body }

      } catch (error) {
        handleApiError(error, { component: 'ListingsStore', action: 'updateListingLocally' })
      }
    },

    async updateListing(body) {
      const cardStore = useCardStore()
      try {
        await apiClient().patch(`/listings/${cardStore.selectedCardData.listingId}`, body) 
      } catch (error) {
        handleApiError(error, { component: 'ListingsStore', action: 'updateListing' })
      }
    },

    async publishListing() {
      const cardStore = useCardStore()

      try {
        // The editors refuse new emoji, but a listing can still carry one from
        // before that rule, and an emoji can stop the update from going
        // through. This is the one publish request that sends the description
        // text itself, so it is cleaned here; card-id-only jobs are rendered
        // server-side and can't be.
        let updatedData = {}

        if (this.activeConnectionData.key === 'ebay') {
          updatedData = {
            ebay_price: cardStore.selectedCardData.ebay_price,
            ebay_title: cardStore.selectedCardData.ebay_title,
            ebay_description: stripEmoji(cardStore.selectedCardData.ebay_description),
            schedule_date: cardStore.selectedCardData.schedule_date,
            schedule_time: cardStore.selectedCardData.schedule_time
         }
        } else if (this.activeConnectionData.key === 'whatnot') {
          updatedData = {
            whatnot_price: cardStore.selectedCardData.whatnot_price,
            whatnot_description: stripEmoji(cardStore.selectedCardData.whatnot_description),
            whatnot_title: cardStore.selectedCardData.whatnot_title,
            quantity: cardStore.selectedCardData.whatnot_quantity_available,
         }
        } else if (this.activeConnectionData.key === 'shopify') {
          updatedData = {
            shopify_price: cardStore.selectedCardData.shopify_price,
            shopify_description: stripEmoji(cardStore.selectedCardData.shopify_description),
            shopify_title: cardStore.selectedCardData.shopify_title,
          }
        } else {
          updatedData = {
            collx_price: cardStore.selectedCardData.collx_price,
            collx_notes: stripEmoji(cardStore.selectedCardData.collx_description),
         }
        }

        const body = {
          data:{
              type: this.activeConnectionData.key,
              listing_id: cardStore.selectedCardData.listingId,
              updated_data: updatedData
          }
      }

        await apiClient().post(`/jobs/update-listing`, body)       
      } catch (error) {
        handleApiError(error, { component: 'ListingsStore', action: 'publishListing' })
      }
    },

    /**
     * End live listings in bulk — CDP-2459.
     *
     * ONE `POST /api/v2/listings/end` for the whole selection: the checked
     * marketplaces go up as a `marketplaces` array rather than fanning out to a
     * request per connection. The server picks the job method itself
     * (remove-quantity for eBay, delete elsewhere), so the FE never sends one.
     *
     * Callers pass payloads built by `buildEndListingPayloads` — already grouped
     * per marketplace and filtered to cards actually live there. Those groups
     * still drive what we send (their union) and how failures are reported;
     * because the wire format is flat, the server is responsible for ignoring
     * any card that isn't live on a given marketplace.
     *
     * @param {Array<{ marketplace: string, card_ids: number[] }>} payloads
     * @returns {Promise<{ ok: boolean, succeeded: Array, failed: Array, cardCount: number }>}
     */
    async endListingsBulk(payloads = [], { bulk = true } = {}) {
      if (!payloads.length) return { ok: true, succeeded: [], failed: [], cardCount: 0 }

      this.endListingsLoading = true
      this.bulkEndInFlight = bulk

      const marketplaces = payloads.map(({ marketplace }) => marketplace)
      const cardIds = [...new Set(payloads.flatMap(({ card_ids }) => card_ids))]

      try {
        const { data } = await apiClient().post(LISTINGS_END_ENDPOINT, {
          marketplaces,
          card_ids: cardIds,
        })

        this.endListingsLoading = false

        return {
          ok: true,
          succeeded: payloads.map((payload) => ({ ...payload, data })),
          failed: [],
          cardCount: cardIds.length,
        }
      } catch (error) {
        handleApiError(error, {
          component: 'ListingsStore',
          action: 'endListingsBulk',
          marketplaces,
        })

        this.endListingsLoading = false
        // Nothing queued → no `listings_ended` event is coming, so don't leave
        // the flag set and mute the next single-listing end.
        this.bulkEndInFlight = false

        return {
          ok: false,
          succeeded: [],
          failed: payloads.map((payload) => ({ ...payload, error })),
          cardCount: 0,
        }
      }
    },

    /**
     * Single-item end — the Listings detail header and the Inventory card detail
     * both route through the same bulk endpoint with a one-element array (which
     * goes up as a single-entry `marketplaces`), so there's exactly one code
     * path to maintain.
     */
    async endListing(type, cardId = null) {
      const cardStore = useCardStore()
      const id = cardId || cardStore.selectedCardData?.card?.id || cardStore.selectedCardData?.id

      if (!id) return { ok: false, succeeded: [], failed: [], cardCount: 0 }

      // `bulk: false` keeps the existing single-listing Pusher behaviour
      // (toast + selectedCardData patch) intact for these call sites.
      return this.endListingsBulk([{ marketplace: type, card_ids: [id] }], { bulk: false })
    },

    async correctListings(type) {
      try {
        this.correctListingsLoader = true

        if (type === 'ebay') {
          await apiClient().post(`/${type}/notifications`)
          await apiClient().post(`/${type}/correct-listings`)
        }

        //this.fetchListings(type)

        this.correctListingsLoader = false
      } catch (error) {
        handleApiError(error, { component: 'ListingsStore', action: 'correctListings' })
        this.correctListingsLoader = false
      }
    },

    async fetchNextPrevIds() {
      const cardStore = useCardStore()

      try {
        this.prevNextIdsLoader = true

        const { data } = await apiClient().get(`/listings/${cardStore.selectedCardData.listingId}/prev-next?type=${this.activeConnectionData.key}${cardStore.selectedCardData.listingStatus === 'ended' ? '&ended=true' : ''}`)

        const { next_listing_id, prev_listing_id, total_listings, current_index } = data

        this.nextListingId = next_listing_id
        this.previousListingId = prev_listing_id

        cardStore.totalCards = total_listings
        cardStore.cardIndex = current_index

        this.prevNextIdsLoader = false
      } catch (error) {
        handleApiError(error, { component: 'ListingsStore', action: 'fetchNextPrevIds' })
        this.prevNextIdsLoader = false
      }
    },

  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useListingsStore, import.meta.hot))
}